import { useState, useEffect, useRef } from "react";
import logoWhite from "../assets/logos/nowquantum-white.png?w=400&format=webp";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "../lib/brand";
import type { NavLink } from "../lib/brand";
import { useLang, usePageTranslations } from "../i18n/context";
import ProductName from "./ProductName";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const { lang, setLang, isTransitioning } = useLang();
  const { t } = usePageTranslations("common");
  const { pathname } = useLocation();
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change (via click)
  const closeMobile = () => setMobileOpen(false);

  // Blur main content when mobile menu is open
  useEffect(() => {
    const main = document.getElementById("main-content");
    if (main) {
      main.style.transition = "filter 0.3s ease";
      main.style.filter = mobileOpen ? "blur(4px) brightness(0.6)" : "none";
    }
    // Prevent body scroll when menu open
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  // Keyboard handler for mobile hamburger button
  const handleHamburgerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape" && mobileOpen) {
      setMobileOpen(false);
      hamburgerRef.current?.focus();
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          // BRAND-GUIDE-V1.0: was "bg-[#2A2A2A]/90 backdrop-blur-md shadow-lg" — matte/no-glass per § 2.4
          ? "bg-[var(--color-bg-base)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]" onClick={closeMobile}>
          <img
            src={logoWhite}
            alt="NowQuantum"
            className="h-10 md:h-12 w-auto"
            width="160"
            height="50"
          />
        </Link>

        {/* Desktop Nav */}
        <nav
          role="navigation"
          aria-label="Main navigation"
          className="hidden lg:flex items-center gap-1 lang-crossfade"
          style={{
            opacity: isTransitioning ? 0 : 1,
            filter: isTransitioning ? "blur(4px)" : "blur(0px)",
          }}
        >
          {NAV_LINKS.map((link) =>
            link.children ? (
              <DropdownItem
                key={link.label}
                link={link}
                isOpen={openDropdown === link.label}
                onMouseEnter={() => handleMouseEnter(link.label)}
                onMouseLeave={handleMouseLeave}
                pathname={pathname}
              />
            ) : (
              <Link
                key={link.label}
                to={link.href!}
                className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-medium text-white/70 hover:text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                aria-current={pathname === link.href ? "page" : undefined}
              >
                {t(link.key)}
              </Link>
            )
          )}
          {/* Language Toggle */}
          <div
            className="ml-3 flex items-center border border-[var(--color-border)] rounded overflow-hidden"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            <button
              onClick={() => setLang("en")}
              aria-label="Switch to English"
              className={`px-2.5 py-1.5 text-[9px] uppercase tracking-wider transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)] ${
                lang === "en"
                  ? "bg-white/15 text-white"
                  : "text-white/75 hover:text-white/70"
              }`}
            >
              EN
            </button>
            <div className="w-px h-4 bg-white/20" />
            <button
              onClick={() => setLang("fr")}
              aria-label="Switch to French"
              className={`px-2.5 py-1.5 text-[9px] uppercase tracking-wider transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)] ${
                lang === "fr"
                  ? "bg-white/15 text-white"
                  : "text-white/75 hover:text-white/70"
              }`}
            >
              FR
            </button>
          </div>

          <Link
            to="/contact"
            className="ml-4 px-5 py-2 bg-[var(--color-brand-primary)] hover:opacity-90 text-white text-[10px] uppercase tracking-[0.15em] font-semibold rounded transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            aria-current={pathname === "/contact" ? "page" : undefined}
          >
            {t("header.requestDemo")}
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          ref={hamburgerRef}
          className="lg:hidden flex flex-col gap-1.5 p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
          onClick={() => setMobileOpen(!mobileOpen)}
          onKeyDown={handleHamburgerKeyDown}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          <span
            className={`block w-6 h-[2px] bg-white transition-transform ${
              mobileOpen ? "rotate-45 translate-y-[5px]" : ""
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-opacity ${
              mobileOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-transform ${
              mobileOpen ? "-rotate-45 -translate-y-[5px]" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu Backdrop - click to dismiss */}
      <div
        className="lg:hidden fixed inset-0 top-16 z-40 transition-opacity duration-300"
        style={{
          background: mobileOpen ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0)",
          pointerEvents: mobileOpen ? "auto" : "none",
        }}
        onClick={closeMobile}
      />

      {/* Mobile Menu */}
      {/* BRAND-GUIDE-V1.0: was className="lg:hidden relative z-50 border-t border-white/15 shadow-2xl" — no drop shadows per § 2.4 */}
      <div
        className="lg:hidden relative z-50 border-t border-[var(--color-border)]"
        style={{
          display: "grid",
          gridTemplateRows: mobileOpen ? "1fr" : "0fr",
          opacity: mobileOpen ? 1 : 0,
          transition: "grid-template-rows 350ms cubic-bezier(0.22,1,0.36,1), opacity 250ms ease",
        }}
      >
        <div className="overflow-hidden bg-[#1a1a1a]">
          <nav
            role="navigation"
            aria-label="Main navigation"
            className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-2 lang-crossfade"
            style={{
              opacity: isTransitioning ? 0 : 1,
              filter: isTransitioning ? "blur(4px)" : "blur(0px)",
            }}
          >
            {NAV_LINKS.map((link) =>
              link.children ? (
                <MobileDropdown
                  key={link.label}
                  link={link}
                  onNavigate={closeMobile}
                  pathname={pathname}
                />
              ) : (
                <Link
                  key={link.label}
                  to={link.href!}
                  onClick={closeMobile}
                  className="py-3 text-[11px] uppercase tracking-[0.15em] font-medium text-white/70 hover:text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  aria-current={pathname === link.href ? "page" : undefined}
                >
                  {t(link.key)}
                </Link>
              )
            )}
            {/* Mobile Language Toggle */}
            <div
              className="mt-4 flex items-center justify-center border border-[var(--color-border)] rounded overflow-hidden self-center"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              <button
                onClick={() => setLang("en")}
                aria-label="Switch to English"
                className={`px-4 py-2 text-[11px] uppercase tracking-wider transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)] ${
                  lang === "en"
                    ? "bg-white/15 text-white"
                    : "text-white/75 hover:text-white/70"
                }`}
              >
                EN
              </button>
              <div className="w-px h-5 bg-white/20" />
              <button
                onClick={() => setLang("fr")}
                aria-label="Switch to French"
                className={`px-4 py-2 text-[11px] uppercase tracking-wider transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)] ${
                  lang === "fr"
                    ? "bg-white/15 text-white"
                    : "text-white/75 hover:text-white/70"
                }`}
              >
                FR
              </button>
            </div>
            <Link
              to="/contact"
              onClick={closeMobile}
              className="mt-4 text-center px-5 py-3 bg-[var(--color-brand-primary)] hover:opacity-90 text-white text-[11px] uppercase tracking-[0.15em] font-semibold rounded transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              aria-current={pathname === "/contact" ? "page" : undefined}
            >
              {t("header.requestDemo")}
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

/* Desktop dropdown */
function DropdownItem({
  link,
  isOpen,
  onMouseEnter,
  onMouseLeave,
  pathname,
}: {
  link: NavLink;
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  pathname: string;
}) {
  const { t } = usePageTranslations("common");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isOpen) {
        onMouseLeave();
      } else {
        onMouseEnter();
      }
    } else if (e.key === "Escape" && isOpen) {
      onMouseLeave();
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-medium text-white/70 hover:text-white transition-colors flex items-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onKeyDown={handleKeyDown}
      >
        {t(link.key)}
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        // BRAND-GUIDE-V1.0: was "absolute top-full left-0 mt-1 py-2 bg-[#1a1a1a] border border-white/15 rounded-lg shadow-xl min-w-[220px]" — no drop shadows per § 2.4
        <div className="absolute top-full left-0 mt-1 py-2 bg-[#1a1a1a] border border-[var(--color-border)] rounded-lg min-w-[220px]">
          {link.children!.map((child) => (
            <Link
              key={child.label}
              to={child.href!}
              className="block px-4 py-2.5 hover:bg-white/5 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
              aria-current={pathname === child.href ? "page" : undefined}
            >
              {child.descKey ? (
                <ProductName
                  name={t(child.key)}
                  className="block text-[11px] uppercase tracking-[0.12em] text-white/90 font-medium"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                />
              ) : (
                <span
                  className="block text-[11px] uppercase tracking-[0.12em] text-white/90 font-medium"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {t(child.key)}
                </span>
              )}
              {child.descKey && (
                <span className="block text-[11px] text-white/75 mt-0.5 font-['Geist_Sans',sans-serif]">
                  {t(child.descKey)}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* Mobile dropdown */
function MobileDropdown({
  link,
  onNavigate,
  pathname,
}: {
  link: NavLink;
  onNavigate: () => void;
  pathname: string;
}) {
  const [open, setOpen] = useState(false);
  const { t } = usePageTranslations("common");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(!open);
    } else if (e.key === "Escape" && open) {
      setOpen(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center justify-between py-3 text-[11px] uppercase tracking-[0.15em] font-medium text-white/70 hover:text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {t(link.key)}
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "grid-template-rows 250ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="overflow-hidden">
          <div className="pl-4 flex flex-col gap-1 mb-2 pt-1">
            {link.children!.map((child) => (
              <Link
                key={child.label}
                to={child.href!}
                onClick={onNavigate}
                className="py-2.5 text-[11px] text-white/75 hover:text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-tertiary)]"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                aria-current={pathname === child.href ? "page" : undefined}
              >
                {t(child.key)}
                {child.descKey && (
                  <span className="text-white/70 ml-2 font-['Geist_Sans',sans-serif]">
                    {t(child.descKey)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
